'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Search, Bell, User, Menu, X, Crown, Download } from 'lucide-react';

interface TopbarProps {
  transparent?: boolean;
}

export default function Topbar({ transparent = false }: TopbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Filmat', href: '/' },
    { label: 'Zhanret', href: '/#genres' },
    { label: 'Premium', href: '/sign-up-login-screen' },
    { label: 'Shkarko APK', href: '/#apk' },
  ];

  const topbarBg =
    transparent && !scrolled
      ? 'bg-transparent' :'bg-background/95 backdrop-blur-md border-b border-border';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${topbarBg}`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 shrink-0">
            <AppLogo size={36} />
            <span className="font-sans font-800 text-xl tracking-tight text-foreground hidden sm:block">
              Dublime Al <span className="text-primary">Shqip</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={`nav-${link.label}`}
                href={link.href}
                className="px-4 py-2 text-sm font-500 text-secondary-foreground hover:text-foreground transition-colors duration-150 rounded-lg hover:bg-muted"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            <button
              className="w-9 h-9 flex items-center justify-center rounded-lg text-secondary-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
              aria-label="Kërko"
            >
              <Search size={18} />
            </button>
            <button
              className="w-9 h-9 flex items-center justify-center rounded-lg text-secondary-foreground hover:text-foreground hover:bg-muted transition-all duration-150 relative"
              aria-label="Njoftime"
            >
              <Bell size={18} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full"></span>
            </button>
            <Link
              href="/sign-up-login-screen"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 active:scale-95 transition-all duration-150"
            >
              <Crown size={14} />
              Premium
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-muted text-secondary-foreground hover:text-foreground hover:bg-border transition-all duration-150"
              aria-label="Profili"
            >
              <User size={18} />
            </Link>
            <button
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-secondary-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute top-16 left-0 right-0 bg-card border-b border-border p-4 fade-in">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={`mobile-nav-${link.label}`}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 text-sm font-500 text-secondary-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-150"
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-2 pt-2 border-t border-border">
                <Link
                  href="/sign-up-login-screen"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 bg-primary text-primary-foreground text-sm font-600 rounded-lg"
                >
                  <Download size={16} />
                  Shkarko APK Falas
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}